import axios from 'axios';

/**
 * Centralized Axios instance for all API communications.
 * Configured with request interceptors for JWT Bearer token injection.
 */
// Use the cloud API URL as a robust fallback if the environment variable is missing
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://velocityrfp-api.engageapp.cloud';

const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'ngrok-skip-browser-warning': 'true'
    }
});

/**
 * Helper to detect if an error is a session expiry/unauthorized error.
 * Checks status code and various backend message formats.
 */
export const isSessionExpiredError = (error) => {
    if (!error.response) return false;
    
    const { status, data } = error.response;
    
    // HTTP 401 is the standard for unauthorized/expired
    if (status === 401) return true;
    
    // Check for common expiry keywords in response body (message, error, msg, or detail)
    const rawDetail = data?.message || data?.error || data?.msg || data?.detail || '';
    let message = '';
    if (typeof rawDetail === 'string') {
        message = rawDetail.toLowerCase();
    } else if (rawDetail && typeof rawDetail === 'object') {
        message = JSON.stringify(rawDetail).toLowerCase();
    }
    
    const expiryKeywords = [
        'expired',
        'invalid token',
        'unauthorized',
        'jwt expired',
        'session expired'
    ];
    
    return expiryKeywords.some(keyword => message.includes(keyword));
};

let isRedirecting = false; // Flag to prevent multiple redirects from concurrent failing requests

/**
 * Global handler for session expiry.
 * Clears data and redirects to login with current path preserved.
 */
export const handleSessionExpired = () => {
    if (isRedirecting) return;
    
    const currentPath = window.location.pathname + window.location.search;
    // Public auth pages have no session to expire. Errors there (wrong password, "OTP expired",
    // "invalid token"...) must be shown on the page, not turned into a "session expired" redirect.
    const publicAuthPaths = ['/login', '/signup', '/forgot-password', '/set-password'];
    const isOnPublicAuthPage = publicAuthPaths.some((p) => window.location.pathname.includes(p));

    // Don't redirect if we are already on a public auth page
    if (isOnPublicAuthPage) return;

    isRedirecting = true;
    
    // 1. Clear sensitive auth data safely
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    
    // 2. Redirect to login with reason and redirect back path
    const loginUrl = `/login?reason=session-expired&redirect=${encodeURIComponent(currentPath)}`;
    window.location.href = loginUrl;
};

// Configure Request Interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("access_token");
        if (token) {
            // Standard Production Header (RFC 6750)
            config.headers.Authorization = `Bearer ${token}`;
        }
        // If sending FormData, delete Content-Type so browser sets boundary automatically
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
            delete config.headers['content-type'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Optional: Response Interceptor (for global error handling like 401 logout)
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (isSessionExpiredError(error)) {
            handleSessionExpired();
        }
        return Promise.reject(error);
    }
);


export default api;
